import * as React from "react"
import { Draggable } from "react-beautiful-dnd"

import { MoveIcon, DeleteIcon } from "./Styles"
import type { ColumnStyle } from "./Styles.Table"
import * as StyledTable from "./Styles.Table"

interface RowProps<ItemValue> {
  item: ItemValue
  index: number
  canDrag: boolean
  onDelete?: (item: ItemValue) => void
  cells: Array<React.ReactNode>
  columnStyles: Array<ColumnStyle | undefined>
}

const RowInner = <ItemValue extends { id: number }>(
  { item, index, canDrag, onDelete, cells, columnStyles }: RowProps<ItemValue>,
  $ref?: React.ForwardedRef<HTMLTableRowElement>,
): JSX.Element => {
  // Both refs are needed: provided.innerRef for react-beautiful-dnd, $ref for outside-click detection on the new row.
  const mergeRefs =
    (innerRef: (element: HTMLElement | null) => void): React.RefCallback<HTMLTableRowElement> =>
    (node) => {
      innerRef(node)
      if (typeof $ref === "function") $ref(node)
      else if ($ref) $ref.current = node
    }

  return (
    <Draggable draggableId={String(item.id)} index={index} isDragDisabled={!canDrag}>
      {(provided, snapshot) => {
        const isBeingDragged = snapshot.isDragging && !snapshot.isDropAnimating

        return (
          <StyledTable.tr
            {...{ ref: mergeRefs(provided.innerRef), ...provided.draggableProps }}
            isBeingDragged={isBeingDragged}
          >
            {cells.map((cell, index) => (
              <StyledTable.td key={index} $style={columnStyles[index]}>
                {cell}
              </StyledTable.td>
            ))}

            {onDelete && (
              <StyledTable.tdDelete>
                <DeleteIcon onClick={() => onDelete(item)} />
              </StyledTable.tdDelete>
            )}

            {canDrag && (
              <StyledTable.tdMove {...provided.dragHandleProps} tabIndex={-1}>
                <MoveIcon />
              </StyledTable.tdMove>
            )}
          </StyledTable.tr>
        )
      }}
    </Draggable>
  )
}

const Row = React.forwardRef(RowInner) as <ItemValue extends { id: number }>(
  props: RowProps<ItemValue> & { ref?: React.ForwardedRef<HTMLTableRowElement> },
) => JSX.Element

export default Row
